import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
   ADD_ROW, DELETE_ROW, UPDATE_ROW, TABLE_HEADER_AMEND_NUMBER, TABLE_HEADER_INSTRUMENT, TABLE_HEADER_PRICE,
   TABLE_HEADER_QTY, TABLE_HEADER_STATUS, JSON_KEY_TRADE_AMEND_NUMBER, JSON_KEY_TRADE_NUMBER, JSON_KEY_INSTRUMENT,
   JSON_KEY_PRICE, JSON_KEY_QTY, JSON_KEY_STATUS, ConvertOrderAndTradeStatus, FormatWithDecimals,
   JSON_KEY_VALUE,
   TABLE_HEADER_VALUE,
   Get10Power,
   JSON_KEY_UPDATE_STATS,
   TABLE_HEADER_MARKET, 
} from '../../common/common'; 
import { selectTableData } from '../../redux/globalsSlice';
import VirtualizedTablesTemplate from './VirtualizedTablesTemplate';

const PublicTrades2 = () => {
   const instrumentsTableData = useSelector(selectTableData);
   const instrumentsTableDataRef = useRef({});
   
   // RAW DATA STORE
   const rawDataStore = useRef(new Map());
   
   // CRITICAL FIX: Message queue BEFORE processing
   const messageQueueRef = useRef([]);
   const flushTimerRef = useRef(null);
   const isMountedRef = useRef(true);

   useEffect(() => {
      instrumentsTableDataRef.current = instrumentsTableData;
   }, [instrumentsTableData]);

   useEffect(() => {
      isMountedRef.current = true;
      return () => {
         isMountedRef.current = false;
         if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
         }
      };
   }, []);

   const handleSingleClick = useCallback((rowData) => {
      // console.log(`[PublicTradesTable] Row Clicked:`, rowData);
   }, []);

   const handleDoubleClick = useCallback((rowData) => {
      // console.log(`[PublicTradesTable] Row Double Clicked:`, rowData);
   }, []);

   const processRow = useCallback((rawData) => {
      const code = rawData.instr;
      const instrumentRow = instrumentsTableDataRef.current[code];
      const price_dec = instrumentRow?.price_dec ?? 0;
      const qty_dec = instrumentRow?.qty_dec ?? 0;
      const value = rawData.qty * rawData.price;

      return {
         ...rawData,
         id: rawData.id,
         price: FormatWithDecimals(rawData.price, price_dec),
         qty: FormatWithDecimals(rawData.qty, qty_dec),
         status: ConvertOrderAndTradeStatus(rawData.status),
         val: FormatWithDecimals(value / Get10Power(qty_dec), price_dec),
         stats: rawData.stats ? 'On' : 'Off',
      };
   }, []);

   // CRITICAL FIX: Queue messages FIRST, process in batch LATER
   const handleEvents = useCallback((details, setLocalTableData) => {
      const { type, data } = details;
      
      // Step 1: Queue the message WITHOUT any processing
      messageQueueRef.current.push({ type, data });

      // Step 2: Start batch timer if not already running
      if (!flushTimerRef.current && isMountedRef.current) {
         flushTimerRef.current = setTimeout(() => {
            // const startTime = performance.now();
            // const queuedMessages = messageQueueRef.current.length;
            
            // console.log(`[PublicTradesTable] Processing batch: ${queuedMessages} queued messages`);

            // Step 3: Process all queued messages into rawDataStore
            for (const { type, data } of messageQueueRef.current) {
               const { t_num, ta_num } = data;
               const id = `${t_num}-${ta_num}`;

               switch (type) {
                  case ADD_ROW:
                     if (!rawDataStore.current.has(id)) {
                        rawDataStore.current.set(id, { ...data, id });
                     }
                     break;
         
                  case UPDATE_ROW:
                     rawDataStore.current.set(id, { ...data, id });
                     break;
         
                  case DELETE_ROW:
                     rawDataStore.current.delete(id);
                     break;

                  default:
                     break;
               }
            }

            // Step 4: Clear the queue
            messageQueueRef.current = [];
            
            // const processingStart = performance.now();
            
            // Step 5: Get all raw data and process in one batch
            const rawArray = Array.from(rawDataStore.current.values());
            const processed = rawArray.map(raw => processRow(raw));
            
            // const processingTime = performance.now() - processingStart;
            
            // Step 6: Single setState with all processed data
            setLocalTableData(processed);
            
            // const totalTime = performance.now() - startTime;
            // console.log(`[PublicTradesTable] Batch complete: ${queuedMessages} messages → ${rawArray.length} unique rows → ${processed.length} processed in ${processingTime.toFixed(0)}ms (total: ${totalTime.toFixed(0)}ms)`);
            
            flushTimerRef.current = null;
         }, 1000); // 1 second batch window
      }
   }, [processRow]);

   const columns = useMemo(() => [
      { Header: 'Trade Number', accessor: JSON_KEY_TRADE_NUMBER },
      { Header: TABLE_HEADER_AMEND_NUMBER, accessor: JSON_KEY_TRADE_AMEND_NUMBER },
      { Header: TABLE_HEADER_INSTRUMENT, accessor: JSON_KEY_INSTRUMENT },
      { Header: TABLE_HEADER_PRICE, accessor: JSON_KEY_PRICE },
      { Header: TABLE_HEADER_QTY, accessor: JSON_KEY_QTY },
      { Header: TABLE_HEADER_VALUE, accessor: JSON_KEY_VALUE },
      { Header: TABLE_HEADER_STATUS, accessor: JSON_KEY_STATUS },
      { Header: TABLE_HEADER_MARKET, accessor: JSON_KEY_UPDATE_STATS },
   ], []);

   return (
      <VirtualizedTablesTemplate
         columns={columns}
         eventHandlers={handleEvents}
         tableName="PublicTradesTable"
         onRowClick={handleSingleClick}
         onRowDoubleClick={handleDoubleClick}
         enableSearch={true}
         defaultSortBy={[
            { id: JSON_KEY_TRADE_NUMBER, desc: false },
         ]}
         enableBatching={false}
      />
   );
};

export default PublicTrades2;